import type { AppDispatch } from '../store';
import { ordersApi } from './ordersApi';
import { tablesApi } from './tablesApi';
import { baseApi } from './baseApi';
import {
  listOfflineQueue, markOfflineEntryFailed, removeOfflineEntry, OfflineQueueEntry,
} from './offlineQueue';
import { removeOfflineOrder } from './offlineOrders';
import { isSyncingOfflineQueue, setSyncingOfflineQueue } from './offlineSyncState';
import { isUnreachableError } from './connectionStatus';
import { getErrorMessage } from '../utils/errorHelpers';

/**
 * Vacía la cola de la caja offline contra el servidor.
 *
 * Se reusan los endpoints de siempre (con `occurredAt`), así que esto hereda el token y su
 * renovación, y el servidor reconoce por Id lo que ya había llegado. El orden importa: la
 * orden se crea antes de que le entren renglones y antes de cobrarse.
 *
 * Tres desenlaces por operación:
 * - **Bien:** se borra de la cola. Cuando a una orden no le queda nada pendiente, se borra
 *   también su copia local y las pantallas vuelven a leer del servidor.
 * - **Sin servidor:** se corta el vaciado y se deja todo pendiente. Volverá a intentarse.
 * - **Rechazo del servidor (4xx):** se marca para revisar, con el mensaje que dio. Lo que
 *   siga de esa misma orden se marca también: mandar el cobro de una orden que el servidor
 *   no aceptó no arregla nada y solo repartiría el problema.
 */
export interface OfflineSyncResult {
  synced: number;
  failed: number;
  /** true si se cortó por falta de servidor y quedan operaciones pendientes. */
  interrupted: boolean;
}

const runEntry = (dispatch: AppDispatch, entry: OfflineQueueEntry): Promise<unknown> => {
  const args = entry.args as any;
  switch (entry.operation) {
    case 'openTable':
      return dispatch(tablesApi.endpoints.openTable.initiate(args)).unwrap();
    case 'createOrder':
      return dispatch(ordersApi.endpoints.createOrder.initiate(args)).unwrap();
    case 'addItems':
      return dispatch(ordersApi.endpoints.addMultipleOrderItems.initiate(args)).unwrap();
    case 'payment':
      return dispatch(ordersApi.endpoints.processPayment.initiate(args)).unwrap();
    case 'createTakeOut':
      return dispatch(ordersApi.endpoints.createTakeOutOrder.initiate(args)).unwrap();
    case 'createDelivery':
      return dispatch(ordersApi.endpoints.createDeliveryOrder.initiate(args)).unwrap();
    case 'tableStatus':
      return dispatch(tablesApi.endpoints.updateTableStatus.initiate(args)).unwrap();
    default:
      return Promise.reject(new Error(`Operación desconocida: ${entry.operation}`));
  }
};

export async function flushOfflineQueue(dispatch: AppDispatch): Promise<OfflineSyncResult> {
  // Un solo vaciado a la vez. El aviso de la barra y la pantalla "Por revisar" montan el
  // mismo hook, así que dos vaciados corrían en paralelo: el primero en terminar apagaba
  // la bandera de sincronización a media pasada del otro, y sus peticiones se volvían a
  // encolar o se marcaban rechazadas con un mensaje falso.
  // El candado se pone ANTES del primer await: leyendo la cola primero, los dos vaciados
  // alcanzaban a pasar por aquí y el candado no servía de nada.
  if (isSyncingOfflineQueue()) {
    return { synced: 0, failed: 0, interrupted: true };
  }
  setSyncingOfflineQueue(true);

  const result: OfflineSyncResult = { synced: 0, failed: 0, interrupted: false };
  const queue = (await listOfflineQueue()).filter((e) => e.status === 'pending');
  if (queue.length === 0) {
    setSyncingOfflineQueue(false);
    return result;
  }

  const blockedOrders = new Set<string>();
  try {
    for (const entry of queue) {
      if (blockedOrders.has(entry.orderId)) {
        await markOfflineEntryFailed(entry, 'No se mandó porque algo anterior de esta misma orden no se pudo sincronizar.');
        result.failed++;
        continue;
      }

      try {
        await runEntry(dispatch, entry);
        await removeOfflineEntry(entry.id);
        result.synced++;
      } catch (error: any) {
        if (isUnreachableError(error)) {
          result.interrupted = true;
          break;
        }
        await markOfflineEntryFailed(entry, getErrorMessage(error, 'El servidor rechazó la operación.'));
        blockedOrders.add(entry.orderId);
        result.failed++;
      }
    }
  } finally {
    setSyncingOfflineQueue(false);
  }

  // Copias locales que ya no hacen falta: lo que queda en la cola manda sobre cuáles siguen.
  const remaining = await listOfflineQueue();
  const stillQueued = new Set(remaining.map((e) => e.orderId));
  const touchedOrders = queue.map((e) => e.orderId).filter((id, i, all) => all.indexOf(id) === i);
  await Promise.all(
    touchedOrders.filter((orderId) => !stillQueued.has(orderId)).map(removeOfflineOrder)
  );

  if (result.synced > 0) {
    dispatch(baseApi.util.invalidateTags([
      'Order', 'Table',
      { type: 'CashRegister' as const, id: 'ACTIVE' },
      { type: 'CashMovement' as const, id: 'LIST' },
    ]));
  }

  return result;
}
